import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import {Filter} from '@gatewee/types/filter';
import {NgbCalendar, NgbDate, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {GateweeDatepickerRangeComponent} from '../../v2/gatewee-datepicker-range/gatewee-datepicker-range.component';
import {TuiDay} from '@taiga-ui/cdk';

export type DateRange = {
  from: NgbDate | null;
  to: NgbDate | null;
};

@Component({
  selector: 'app-filter-head',
  templateUrl: './filter-head.component.html',
  styleUrls: ['./filter-head.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterHeadComponent {
  @Input() filters: Filter[] = [];
  @Input() enableDateRange = true;
  @Input() activeToggle = false;
  @Output() filterApplied = new EventEmitter<Record<string, unknown>>();
  @Output() filterRemoved = new EventEmitter<void>();
  @Output() filtersChanged = new EventEmitter<void>();
  @Output() expandedFilters = new EventEmitter<boolean>();

  navigation = 'arrows';
  outsideDays = 'visible';

  loading = false;

  filterPayload: Record<string, unknown> = {};

  @ViewChild('dateRangePicker')
  dateRangePicker!: GateweeDatepickerRangeComponent;
  calendar = inject(NgbCalendar);
  dateRange?: DateRange;

  validateFilters(): boolean {
    return this.filters.every(filter => {
      if (filter.type === 'text' || filter.type === 'select') {
        return filter.value !== undefined && filter.value !== null;
      }
      if (filter.type === 'date') {
        return !!filter.value;
      }
      return true;
    });
  }

  applyFilters(): void {
    this.filterPayload = {
      ...this.filters.reduce((acc: Record<string, unknown>, filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ''
        ) {
          acc[filter.key] = this.formatFilterValue(filter);
        }
        return acc;
      }, {}),
    };

    if (this.enableDateRange && this.dateRange) {
      const {from, to} = this.dateRange;

      if (from && to) {
        this.filterPayload['options.startDate'] = `${from.year}-${from.month
          .toString()
          .padStart(2, '0')}-${from.day.toString().padStart(2, '0')}`;
        this.filterPayload['options.endDate'] = `${to.year}-${to.month
          .toString()
          .padStart(2, '0')}-${to.day.toString().padStart(2, '0')}`;
      }
    }

    this.filtersChanged.emit();
    this.filterApplied.emit(this.filterPayload);
  }

  private formatFilterValue(filter: Filter): unknown {
    if (filter.type === 'date' && filter.value) {
      // Handle string format (from gatewee-datepicker-v2)
      if (typeof filter.value === 'string') {
        return filter.value;
      }
      // Handle NgbDateStruct format
      const {year, month, day} = filter.value as NgbDateStruct;
      return `${year}-${month.toString().padStart(2, '0')}-${day
        .toString()
        .padStart(2, '0')}`;
    }
    return filter.value;
  }

  removeFilters(): void {
    this.filters = this.filters.map(filter => ({
      ...filter,
      value: filter.type === 'date' ? null : '',
    }));
    this.dateRange = undefined;
    if (this.enableDateRange && this.dateRangePicker) {
      this.dateRangePicker.control.setValue(null);
    }
    this.filtersChanged.emit();
    this.filterRemoved.emit();
  }

  trackByFilter(index: number, filter: Filter): string {
    return filter?.key ?? `filter-${index}`;
  }

  toggleExpanded(): void {
    this.activeToggle = !this.activeToggle;
    this.expandedFilters.emit(this.activeToggle);
  }

  getDaterange(): DateRange {
    const firstDayOfMonth = this.calendar.getPrev(
      this.calendar.getToday(),
      'm',
      1,
    );
    const lastDayOfMonth = this.calendar.getNext(
      this.calendar.getToday(),
      'm',
      1,
    );

    return {
      from: firstDayOfMonth,
      to: lastDayOfMonth,
    };
  }

  setDateRange(dateRange: {from: TuiDay | null; to: TuiDay | null}): void {
    // Convert TuiDay to NgbDate
    this.dateRange = {
      from: dateRange.from
        ? NgbDate.from({
            year: dateRange.from.year,
            month: dateRange.from.month + 1, // TuiDay uses 0-based months
            day: dateRange.from.day,
          })
        : null,
      to: dateRange.to
        ? NgbDate.from({
            year: dateRange.to.year,
            month: dateRange.to.month + 1, // TuiDay uses 0-based months
            day: dateRange.to.day,
          })
        : null,
    };
    this.filtersChanged.emit();
  }

  // Helper to convert TuiDay string (ISO format) to NgbDateStruct
  private convertStringToNgbDateStruct(
    dateString: string,
  ): NgbDateStruct | null {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    return {year, month, day};
  }
}
